/* eslint-disable no-restricted-syntax */
import { axios } from '@complispace/cs-design-system';

const baseURL = process.env.CONTENT_ENGINE_URL;
const moduleBaseUrl = `${baseURL}/app/modules`;
const clientBaseUrl = `${baseURL}/app/client`;
const contentBaseUrl = `${baseURL}/app/contents`;

function moduleUrl(moduleId, branchName) {
  return `${moduleBaseUrl}/${moduleId}/branches/${branchName}`;
}

function visiblePageUrl(moduleId, pagePath, visible) {
  return `${moduleBaseUrl}/${moduleId}/toc/page/${pagePath}/visible/${visible}`;
}

function visibleTOCUrl(moduleId) {
  return `${moduleBaseUrl}/${moduleId}/toc/pages`;
}

function publicTOCUrl(moduleId, path) {
  return `${moduleBaseUrl}/${moduleId}/pages/${path}/public`;
}

function customModuleUrl(moduleId, path) {
  return `${moduleBaseUrl}/${moduleId}/pages/${path}`;
}

export default {
  // GET /app/modules
  async getActivatedCEModules(type) {
    return axios.get(`${moduleBaseUrl}?type=${type}`);
  },

  // GET /app/modules/admin-ugc
  async getActivatedUGCModules() {
    return axios.get(`${moduleBaseUrl}/admin-ugc`);
  },

  // POST /app/modules
  async createModule(params) {
    return axios.post(moduleBaseUrl, params);
  },

  // POST /app/modules/{moduleId}/activate
  async activateClientCustomModule(moduleId) {
    return axios.post(`${moduleBaseUrl}/${moduleId}/activate`);
  },

  // POST /app/modules/{moduleId}/deactivate
  async deactivateClientCustomModule(moduleId) {
    return axios.post(`${moduleBaseUrl}/${moduleId}/deactivate`);
  },

  // GET /app/modules/{moduleId}/branches/{branchName}/toc
  async getToc(moduleId, branchName) {
    return axios.get(`${moduleUrl(moduleId, branchName)}/toc?json=true`);
  },

  // GET /app/modules/branches/{branchName}/toc
  async getTocs(moduleIds, branch) {
    return axios.get(`${moduleBaseUrl}/branches/${branch}/toc`, {
      params: {
        moduleIds
      }
    });
  },

  // PUT /app/modules/{moduleId}/toc/pages
  async setPageProperties(moduleId, pagesProperties) {
    const result = await axios.put(visibleTOCUrl(moduleId), {
      pages: pagesProperties
    });
    return result;
  },

  // PUT /app/modules/{moduleId}/pages/{page}/public
  async setTOCPublic(moduleId, path, isPublic) {
    const result = await axios.put(publicTOCUrl(moduleId, path), {
      isPublic
    });

    return result;
  },

  // POST /app/modules/{moduleId}/pages/{path}
  async createPage(moduleId, path, title, content) {
    const data = { title, content };
    return axios.post(`${moduleBaseUrl}/${moduleId}/pages/${path}`, data);
  },

  // FIXME: Concurrency issue
  // The backend requires multple calls to be sequential due to concurrency
  // issues. This two bulk functions are inefficient and should be handled
  // on the backend
  // GET /app/modules/{moduleId}/toc/page/{pagePath}/visible/{visibility}
  async setMultipleVisible(moduleId, rootPath, visiblePagePaths) {
    await axios.put(visiblePageUrl(moduleId, rootPath, false));
    for (const page of visiblePagePaths) {
      // eslint-disable-line
      await axios.put(visiblePageUrl(moduleId, page, true)); // eslint-disable-line
    }
  },

  // GET /app/modules/{moduleId}/toc/page/{pagePath}/visible/{visibility}
  // In most cases suspect more efficient to make multiple INvisible.
  // Users likely hide fewer policy paths in a module than are shown
  // resulting in fewer sequential calls.
  // However, for this to work the array must be in parent->child border
  // Setting parent visiblity on backend sets cascades to children
  async setMultipleInvisible(moduleId, rootPath) {
    await axios.put(visiblePageUrl(moduleId, rootPath, true));
    // eslint-disable-next-line no-undef
    for (const page of visiblePagePaths) {
      // eslint-disable-line
      await axios.put(visiblePageUrl(moduleId, page, false)); // eslint-disable-line
    }
  },

  // GET /app/modules/{moduleId}/branch/{branchName}/toc
  async setVisible(moduleId, pagePath, visible) {
    return axios.put(visiblePageUrl(moduleId, pagePath, visible));
  },

  // GET /app/modules/{moduleId}/pages/{page}/details
  async getPageDetails(moduleId, pagePath) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/pages/${pagePath}/details`);
  },

  // GET /app/modules/{moduleId}/branches/{branchName}/pages/{pagePath}
  async getPage(
    moduleId,
    branchName,
    pagePath,
    includeChildren,
    pageCommitOID,
    pageCommitTimestamp,
    isPublicViewMode
  ) {
    const queryParametersMap = new Map();
    const queryParameters = [];

    queryParametersMap.set('includeChildren', includeChildren);
    queryParametersMap.set('pageCommitOID', pageCommitOID);
    queryParametersMap.set('pageCommitTimestamp', pageCommitTimestamp);
    queryParametersMap.set('isPublicViewMode', isPublicViewMode);

    queryParametersMap.forEach((value, key) => {
      if (value) {
        queryParameters.push(`${key}=${value}`);
      }
    });

    let queryParametersString = '';

    if (queryParameters.length) {
      queryParametersString = queryParameters.join('&');
      queryParametersString = `?${queryParametersString}`;
    }

    return axios.get(
      `${moduleUrl(moduleId, branchName)}/pages/${pagePath}${queryParametersString}`
    );
  },

  // GET /app/modules/{moduleId}/pages/{path}/assurance-details
  async getPageAssuranceDetails(moduleId, path) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/pages/${path}/assurance-details`);
  },

  // GET /app/exports/modules/{moduleId}/branches/{branchName}?showContextKey=true
  async getModuleExports(moduleId, moduleName, branchName, showContextKey = false) {
    const response = await axios.get(
      `${baseURL}/app/exports/modules/${moduleId}/branches/${branchName}?showContextKey=${showContextKey}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${moduleName}.pdf`);
    document.body.appendChild(link);
    link.click();
  },

  // POST /app/modules/exports
  async exportModulePages(modules, fileName, showContextKey = false) {
    const payload = {
      modules,
      showContextKey
    };
    const response = await axios.post(`${baseURL}/app/modules/exports`, payload, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
  },

  // GET /app/modules/{moduleId}/pages/{path}/history
  async getPageHistory(moduleId, path) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/pages/${path}/history`);
  },

  // DELETE /app/modules/{moduleId}/pages/{path}
  async deleteCustomModule(moduleId, path) {
    return axios.delete(customModuleUrl(moduleId, path));
  },

  // PUT /app/modules/{moduleId}
  async updateClientCustomModule(moduleId, payload) {
    return axios.put(`${moduleBaseUrl}/${moduleId}`, payload);
  },

  // PUT /app/modules/{moduleId}/module-details
  async updateAppModule(moduleId, payload) {
    return axios.put(`${moduleBaseUrl}/${moduleId}/module-details`, payload);
  },

  // DELETE /app/modules/{moduleId}
  async deleteClientCustomModule(moduleId) {
    return axios.delete(`${moduleBaseUrl}/${moduleId}`);
  },

  // PUT /app/modules/{moduleId}/pages/{path}
  async updateClientCustomModulePage(moduleId, path, payload) {
    return axios.put(customModuleUrl(moduleId, path), payload);
  },

  // PUT /app/modules/{moduleId}/pages/{path}/revert
  async revertClientCustomPageContent(moduleId, path, payload) {
    return axios.put(`${customModuleUrl(moduleId, path)}/revert`, payload);
  },

  // POST /app/contents/file
  async uploadImage(data) {
    return axios.post(`${contentBaseUrl}/file`, data);
  },

  getUploadImageURL() {
    return `${contentBaseUrl}/images`;
  },

  async deleteImage(hashCode) {
    return axios.delete(`${contentBaseUrl}/images/${hashCode}`);
  },

  // GET /app/contents/images/{entityTag}
  async getImage(hashCode) {
    return axios.get(`${contentBaseUrl}/images/${hashCode}`);
  },

  // GET /app/modules/unpublishedUpdates
  async getUnpublishedUpdates(moduleIds, excludePublishedModules) {
    const queryParametersMap = new Map();
    const queryParameters = [];

    queryParametersMap.set('moduleIds', moduleIds);
    queryParametersMap.set('excludePublishedModules', excludePublishedModules);

    queryParametersMap.forEach((value, key) => {
      if (value) {
        queryParameters.push(`${key}=${value}`);
      }
    });

    let queryParametersString = '';

    if (queryParameters.length) {
      queryParametersString = queryParameters.join('&');
      queryParametersString = `?${queryParametersString}`;
    }

    return axios.get(`${moduleBaseUrl}/unpublishedUpdates${queryParametersString}`);
  },

  // GET /app/modules/status
  async getModulesStatusData(moduleIds, includeChildren) {
    const queryParametersMap = new Map();
    const queryParameters = [];

    queryParametersMap.set('moduleIds', moduleIds);
    queryParametersMap.set('includeChildren', includeChildren);

    queryParametersMap.forEach((value, key) => {
      if (value) {
        queryParameters.push(`${key}=${value}`);
      }
    });

    let queryParametersString = '';

    if (queryParameters.length) {
      queryParametersString = queryParameters.join('&');
      queryParametersString = `?${queryParametersString}`;
    }

    return axios.get(`${moduleBaseUrl}/status${queryParametersString}`);
  },

  // GET /app/modules/{moduleId}/allacceptedpublishes
  async getAllModuleAcceptedPublishes(moduleId) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/allacceptedpublishes`);
  },

  // POST /app/contents/search
  async searchElasticSearchContent(payload) {
    return axios.post(`${contentBaseUrl}/search`, payload);
  },

  // POST /app/contents/search-quicklink
  async searchElasticSearchQuicklinks(payload) {
    return axios.post(`${contentBaseUrl}/search-quicklink`, payload);
  },

  // PUT /app/modules/{moduleId}/report-details
  async updateAssuranceReportDetails(moduleId, tocJson) {
    return axios.put(`${moduleBaseUrl}/${moduleId}/report-details`, { toc: tocJson });
  },

  async exportModuleTOCs(modules, fileName) {
    const payload = {
      modules
    };

    const response = await axios.post(`${baseURL}/app/modules/toc/exports`, payload, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
  },

  // POST /app/modules/{moduleId}/restore-archived-pages
  async restoreModulePages(moduleId, pages) {
    const payload = {
      pages
    };

    return axios.post(`${moduleBaseUrl}/${moduleId}/restore-archived-pages`, payload);
  },

  // DELETE /app/modules/{moduleId}/ugc-archived-pages
  async deleteModulePages(moduleId, pages) {
    return axios.delete(`${moduleBaseUrl}/${moduleId}/ugc-archived-pages`, {
      params: {
        pages
      }
    });
  },

  // GET /app/modules/{moduleId}/ugc-archived-pages/{path}
  async viewModuleArchivedPage(moduleId, page) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/ugc-archived-pages/${page}`);
  },

  async saveDraftTOC(moduleId, tocJson) {
    return axios.post(`${moduleBaseUrl}/${moduleId}/toc/draft-reordering`, { toc: tocJson });
  },

  // GET /app/module/{moduleId}/pages/{path}/differences
  async getPageDifferencesFromCommit(moduleId, page, commitOID, timeStamp) {
    return axios.get(`${moduleBaseUrl}/${moduleId}/pages/${page}/differences`, {
      params: {
        pageCommitOID: commitOID,
        pageCommitTimestamp: timeStamp
      }
    });
  },
  async getContentSearchFilter() {
    return axios.get(`${clientBaseUrl}/search-filters`, {
      params: {
        filter_type: 'content-register'
      }
    });
  },
  async createContentSearchFilter(body) {
    return axios.post(`${clientBaseUrl}/search-filters`, body);
  },
  async updateContentSearchFilter(filterId, payload) {
    return axios.put(`${clientBaseUrl}/search-filters/${filterId}`, payload);
  },
  async deleteContentSearchFilter(filterId) {
    return axios.delete(`${clientBaseUrl}/search-filters/${filterId}`);
  }
};
